export type PublicNavItem = {
  to: string;
  label: string;
  end: boolean;
  disabled?: boolean;
  status?: string;
  activePaths?: readonly string[];
  activePrefix?: string;
  excludeActivePrefixes?: readonly string[];
};

export type FeatureTweetFeatureKey =
  | "home"
  | "inscribe"
  | "launch"
  | "mint"
  | "auctions"
  | "tools"
  | "batch"
  | "gallery"
  | "runes"
  | "history"
  | "broadcast"
  | "transfer-inscriptions"
  | "transfer-utxo"
  | "isolate-utxo"
  | "advanced-splits"
  | "split-runes"
  | "burn-inscriptions";

export type FeatureTweetFeature = {
  key: FeatureTweetFeatureKey;
  label: string;
  route: string;
  defaultPostText: string;
};

export const INSCRIBE_NAV_ITEMS: readonly PublicNavItem[] = [
  { to: "/inscribe", label: "Inscribe", end: false },
  { to: "/batch", label: "Batch", end: false },
  { to: "/gallery", label: "Gallery", end: false },
  { to: "/runes", label: "Runes", end: false },
  { to: "/history", label: "History", end: false },
  { to: "/console", label: "Broadcast", end: false },
];

const INSCRIBE_NAV_PATHS = INSCRIBE_NAV_ITEMS.map((item) => item.to);
const LAUNCH_NAV_PATHS = [
  "/launchpad/launch",
  "/launchpad/create",
  "/launchpad/manage",
  "/launchpad/admin",
  "/launchpad/profile",
  "/launchpad/workspace",
];
const AUCTION_NAV_PATHS = [
  "/auctions",
  "/launchpad/launch/auctions",
];
const SHIELDED_NAV_PATHS = [
  "/shield",
  "/shielded-hauntlings",
  "/launchpad/shielded",
];

export const PUBLIC_NAV_ITEMS: readonly PublicNavItem[] = [
  { to: "/inscribe", label: "Inscribe", end: true, activePaths: INSCRIBE_NAV_PATHS },
  { to: "/launchpad/launch", label: "Launch", end: false, activePaths: LAUNCH_NAV_PATHS, excludeActivePrefixes: AUCTION_NAV_PATHS },
  { to: "/launchpad", label: "Mint", end: true, activePrefix: "/launchpad", excludeActivePrefixes: [...LAUNCH_NAV_PATHS, ...SHIELDED_NAV_PATHS] },
  { to: "/shield", label: "Shielded", end: false, activePaths: SHIELDED_NAV_PATHS },
  { to: "/auctions", label: "Auctions", end: false, activePaths: AUCTION_NAV_PATHS },
  { to: "/tools", label: "Tools", end: false },
  { to: "/rare-sats", label: "Rare sats", end: false },
  { to: "/marketplacehiddenfuckoff", label: "Market", end: false, disabled: true, status: "Soon" },
];

export const FEATURE_TWEET_FEATURES: readonly FeatureTweetFeature[] = [
  {
    key: "home",
    label: "Spam The Chain",
    route: "/",
    defaultPostText: "Spam The Chain will regularly showcase the Bitcoin and Ordinals features live across the app. Follow along for a clear tour of what you can create, launch, mint and manage.",
  },
  {
    key: "inscribe",
    label: "Inscribe",
    route: "/inscribe",
    defaultPostText: "Create a single Bitcoin Ordinals inscription from your wallet with clear fee, postage and review steps before you sign.",
  },
  {
    key: "launch",
    label: "Launch",
    route: "/launchpad/launch",
    defaultPostText: "Launchpad gives creators a place to set up Bitcoin drops, manage presentation details and prepare mint flows before collectors arrive.",
  },
  {
    key: "mint",
    label: "Mint",
    route: "/launchpad",
    defaultPostText: "The Mint view is where collectors discover public Spam The Chain drops and follow each drop from preview to mint.",
  },
  {
    key: "auctions",
    label: "Auctions",
    route: "/auctions",
    defaultPostText: "Auctions brings timed Bitcoin-native listings into Spam The Chain with bidding, buy-now options and settlement tracking.",
  },
  {
    key: "tools",
    label: "Tools",
    route: "/tools",
    defaultPostText: "Tools collects focused Bitcoin utilities for Spam The Chain users, including burn and transaction workflows with a clear review step before signing.",
  },
  {
    key: "batch",
    label: "Batch Inscriptions",
    route: "/batch",
    defaultPostText: "Batch up to 500 Ordinals in one organised flow, with per-item destinations, metadata, CSV import and rare-sat selection. Spam The Chain adds no inscription service fee; you still pay Bitcoin network fees and postage.",
  },
  {
    key: "gallery",
    label: "Ordinals Galleries",
    route: "/gallery",
    defaultPostText: "Build an on-chain Ordinals gallery from inscription IDs, add a cover, collection details and traits, then preview it before signing. Spam The Chain adds no inscription service fee; Bitcoin network fees and postage still apply.",
  },
  {
    key: "runes",
    label: "Create a Rune",
    route: "/runes",
    defaultPostText: "Define a Bitcoin Rune’s supply, premine, public mint terms and identity in one flow. Check name availability, review the total, sign once, and the etch broadcasts automatically after six confirmations.",
  },
  {
    key: "history",
    label: "Inscription History",
    route: "/history",
    defaultPostText: "Your Spam The Chain inscription history keeps the payment and reveal transactions, every inscription ID and confirmation state together—plus copy-all and batch-to-drop actions when you need them.",
  },
  {
    key: "broadcast",
    label: "Broadcast Transactions",
    route: "/console",
    defaultPostText: "Already have signed transaction hex or a Spam The Chain recovery package? The Broadcast tool can send a raw transaction or sequence the payment and reveal transactions from the package.",
  },
  {
    key: "transfer-inscriptions",
    label: "Transfer Inscriptions",
    route: "/tools/transfer-inscriptions",
    defaultPostText: "Send one or many inscriptions in one reviewed transaction. Choose a destination for every ordinal, then use Copy to Others when several are going to the same wallet.",
  },
  {
    key: "transfer-utxo",
    label: "Transfer UTXO",
    route: "/tools/transfer-utxo",
    defaultPostText: "Move a complete Bitcoin UTXO exactly as it is—including every sat, inscription or Rune it contains—with wallet inventory checks and a server-verified transaction review before signing.",
  },
  {
    key: "isolate-utxo",
    label: "Isolate UTXO",
    route: "/tools/isolate-utxo",
    defaultPostText: "Need the first sats separated from a clean UTXO? Isolate UTXO lets you choose the output size and destination, then shows the network and service fees before signing.",
  },
  {
    key: "advanced-splits",
    label: "Advanced Splits",
    route: "/tools/advanced-splits",
    defaultPostText: "Turn one clean UTXO into exact outputs: choose every destination and sat amount, or build equal bundles around notable sats. Review the finished transaction before your wallet signs it.",
  },
  {
    key: "split-runes",
    label: "Split Runes",
    route: "/tools/split-runes",
    defaultPostText: "Split one Rune-bearing UTXO into multiple destination outputs with exact Rune amounts and sat values. Spam The Chain checks the balance, edict and output plan before signing.",
  },
  {
    key: "burn-inscriptions",
    label: "Burn Inscriptions",
    route: "/tools/burn",
    defaultPostText: "Burn up to 50 owned inscriptions in one Bitcoin transaction, with clear warnings for parent collections and special sats. Nothing broadcasts until you complete the irreversible-action review.",
  },
];

export function publicFeatureForTweet(key: string): FeatureTweetFeature | null {
  return FEATURE_TWEET_FEATURES.find((feature) => feature.key === key) ?? null;
}
